import { supabase } from '@/lib/supabase';
import { isCell } from '@/lib/cell/config';
import { cellGet, cellPost } from '@/lib/cell/api';
import { getCellSession } from '@/lib/cell/cognito';

/**
 * THE SINGLE WRITE PATH FOR MY INFO. Every editable card on this page — contact details,
 * emergency contact, licence and COI — saves through the pro-profile-update Edge Function.
 * supabase.functions.invoke attaches the caller's session JWT automatically; the function
 * resolves the Pro from that token, writes through the pro-scoped database functions
 * (pro_update_own_profile / pro_add_own_compliance, called as the caller, so the database
 * remains the security boundary), and then mirrors the change into Zoho.
 *
 * `patch` carries either tenant fields (phone, home_address, emergency_*, instagram_handle)
 * or a compliance filing (record_type + document_number + document_type / carrier /
 * expiry_date) — the function accepts both and ignores anything it does not recognise.
 *
 * Peter, 2026-08-07: "any changes that we make into my info gets written into Zoho."
 */
export async function saveProProfile(
  patch: Record<string, string | string[] | null>,
  tenantId: string | null,
): Promise<Record<string, unknown> | null> {
  // STATE THE ROW. A pro can have several tenant rows — on Sharon most carry BOTH the live
  // sharon_zoho row and the legacy sharon_ma one. Without this the function had to guess, and it
  // guessed the legacy row while this screen rendered the live one, so an edit "saved" and the
  // page never changed. The function verifies the row is ours AND that we belong to its studio.
  if (isCell()) {
    // CELL (M28): POST /v1/pro-hub/profile — pro-profile-update's database leg on the AWS cell (same body, same
    // { ok, tenant, compliance, zoho } answer; the CRM mirror does not run there). Refused while viewing as a pro.
    const { data: cellData, error: cellErr } = await cellPost<Record<string, unknown>>('/v1/pro-hub/profile', {
      ...patch, ...(tenantId ? { tenant_id: tenantId } : {}),
    });
    if (cellErr) throw new Error(cellErr.message);
    return checkSaved(patch, await withCrmMirror(cellData));   // 958b001e: the CRM copy's own outcome (withCrmMirror below)
  }
  const { data, error } = await supabase.functions.invoke('pro-profile-update', {
    body: { ...patch, ...(tenantId ? { tenant_id: tenantId } : {}) },
  });
  if (error) {
    // Surface the function's JSON { ok:false, error } body when present —
    // FunctionsHttpError carries the raw Response on `.context`.
    let msg = error.message;
    try {
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        const body = await ctx.json();
        if (body?.error) msg = body.error as string;
      }
    } catch { /* keep the generic message */ }
    throw new Error(msg);
  }
  return checkSaved(patch, data as Record<string, unknown> | null);
}

/** The answer's checks, shared by both paths: { ok:false } is a failure, and so is a clear that did not land. */
function checkSaved(
  patch: Record<string, string | string[] | null>,
  data: Record<string, unknown> | null,
): Record<string, unknown> | null {
  if (data && data.ok === false) {
    throw new Error((data.error as string) ?? 'Save failed');
  }
  const out = (data as Record<string, unknown> | null) ?? null;
  // HUB-029: "Saved" only when the studio's record now says what the pro typed. The door answers
  // with the tenant row as it stands after the write; a field the pro erased must come back empty.
  // (The door used to send an erased field as "keep", so the old bio, phone or emergency contact
  // stayed while this screen flashed Saved.)
  const unchanged = clearsThatDidNotLand(patch, out?.tenant);
  if (unchanged.length > 0) {
    throw new Error(
      `Not saved: the studio's record still shows your old ${unchanged.join(', ')}. ` +
      'Your edit is still here — try Save again, or tell the studio if it keeps happening.',
    );
  }
  return out;
}

/** The tenant fields My Info edits, with the words a pro reads in an error. */
export const PROFILE_FIELD_LABELS: Record<string, string> = {
  phone: 'phone',
  home_address: 'home address',
  emergency_contact_name: 'emergency contact name',
  emergency_contact_phone: 'emergency contact phone',
  emergency_contact_relationship: 'emergency contact relationship',
  instagram_handle: 'Instagram handle',
  short_bio: 'bio',
  services_offered: 'services',
};

/**
 * The profile fields the pro ERASED (sent as an empty string) that the returned tenant row still
 * holds a value for. Only clears are compared: a typed value can legitimately come back reshaped
 * (the database rewrites phone numbers to +1… form, trg_tenants_phone_e164), a clear cannot.
 * No row back (a compliance-only save) means nothing to compare.
 */
export function clearsThatDidNotLand(
  patch: Record<string, string | string[] | null>,
  tenant: unknown,
): string[] {
  if (!tenant || typeof tenant !== 'object') return [];
  const row = tenant as Record<string, unknown>;
  const out: string[] = [];
  for (const [key, label] of Object.entries(PROFILE_FIELD_LABELS)) {
    if (!(key in patch)) continue;
    const sent = patch[key];
    const cleared = sent === null || (typeof sent === 'string' && sent.trim() === '');
    if (!cleared) continue;
    const raw = row[key];
    const have = raw === null || raw === undefined ? '' : String(raw).trim();
    if (have !== '') out.push(label);
  }
  return out;
}

/**
 * HUB-068: the door answers { ok: true, …, zoho } AFTER the database write, and `zoho.contact` /
 * `zoho.application` / `zoho.onboarding` say "error" when the CRM mirror failed (image-studios-backend
 * supabase/functions/pro-profile-update/index.ts, the write-back blocks). The local save stands; the
 * screen must not call the whole thing done. Returns the words to show, or null when nothing failed.
 */
const CRM_TARGETS: Array<[string, string]> = [
  ['contact', 'contact record'], ['application', 'application'], ['onboarding', 'onboarding record'],
];
export function crmMirrorProblem(out: unknown): string | null {
  const z = (out as { zoho?: Record<string, unknown> } | null | undefined)?.zoho;
  if (!z || typeof z !== 'object') return null;
  const failed = CRM_TARGETS.filter(([k]) => z[k] === 'error').map(([, label]) => label);
  const notes = Array.isArray(z.notes) ? (z.notes as unknown[]).map(String) : [];
  const aborted = notes.some((n) => n.startsWith('zoho: '));
  if (!failed.length && !aborted) return null;
  const what = failed.length ? `your ${failed.join(' and ')} in the studio's CRM` : "the studio's CRM";
  // HUB-068 (fix-p2): pro-profile-update now QUEUES a failed Zoho write and keeps retrying it
  // (zoho.retry_queued lists the modules). Only when every failed target is queued may the words say
  // it will be retried; anything not queued keeps the old advice.
  const MODULE_OF: Record<string, string> = { contact: 'Contacts', application: 'Tenant_Application', onboarding: 'Tenant_Onboarding' };
  const queued = Array.isArray(z.retry_queued) ? (z.retry_queued as unknown[]).map(String) : [];
  const allQueued = !aborted && failed.length > 0 &&
    CRM_TARGETS.filter(([k]) => z[k] === 'error').every(([k]) => queued.includes(MODULE_OF[k]));
  if (allQueued) {
    return `Saved here — ${what} did not update yet. It will keep trying on its own; nothing more to do.`;
  }
  return `Saved here — but ${what} did not update, so the studio may still see the old details there. Save again to retry, or tell the studio.`;
}

/**
 * 958b001e (Cora 228761e8, the parity floor; aws-reviewer 7a373c8c): on the AWS cell the CRM copy runs after the save, in
 * tenean-worker-pro-profile-mirror, which records production's own §6 block (reduced: statuses, retry_queued, note labels) for
 * the save's event (tenean-infra cell 0200). My Info's load returns it as `crm_mirror`. Wait for the one whose event_id is
 * THIS save's `mirror_event`, up to CRM_MIRROR_WAIT_MS (Cora's approved difference: if none arrives in time, the save shows
 * no CRM note and the next load shows it). Returns the block, or null.
 */
export const CRM_MIRROR_WAIT_MS = 5000;
/** Production wrote Zoho before answering; on the cell the copy runs a moment later in the mirror worker, so the save's answer
 *  takes ITS outcome (matched on the save's own event) as `zoho`, and crmMirrorProblem at every card is production's own. */
export async function withCrmMirror(
  cellData: Record<string, unknown> | null,
  readAt: (locationId: string) => Promise<CrmMirror> = readCrmMirror,
): Promise<Record<string, unknown> | null> {
  const loc = typeof cellData?.mirror_location === 'string' ? cellData.mirror_location : null;
  const outcome = loc ? await waitForCrmMirror(cellData?.mirror_event, () => readAt(loc)) : null;
  return outcome ? { ...cellData, zoho: outcome } : cellData;
}
type CrmMirror = { event_id?: string; block?: Record<string, unknown> } | null | undefined;
async function readCrmMirror(locationId: string): Promise<CrmMirror> {
  const s = await getCellSession();
  if (!s) return null;
  // CELL: GET /v1/pro-hub/locations/{locationId}/users/{authUserId}/my-info (myInfoLoad) for the studio the save named
  // (mirror_location), so a multi-studio pro's save never reads another tenancy's outcome (aws-reviewer, #172)
  const { data } = await cellGet<{ crm_mirror?: CrmMirror }>(
    `/v1/pro-hub/locations/${encodeURIComponent(locationId)}/users/${s.userId}/my-info`);
  return data?.crm_mirror ?? null;
}
export async function waitForCrmMirror(
  eventId: unknown,
  read: () => Promise<CrmMirror>,
  waitMs = CRM_MIRROR_WAIT_MS, everyMs = 500, now: () => number = Date.now,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<Record<string, unknown> | null> {
  if (typeof eventId !== 'string' || !eventId) return null;
  const until = now() + waitMs;
  for (;;) {
    const m = await read().catch(() => null);
    if (m?.event_id === eventId && m.block && typeof m.block === 'object') return m.block;
    if (now() + everyMs > until) return null;
    await sleep(everyMs);
  }
}
